import { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import { LeadMagnetApi } from "../../service";
import Logo from "../assets/Logo.png";
import LogoFull from "../assets/LogoFull.png";
import HeroBg from "../assets/Home/LandingPageBg.png";
import CheckCircle from "../assets/Home/CheckCircle.svg";
import "./LeadMagnet.css";

const CAMPAIGN_SLUG = "home-maintenance-assessment";

const schema = Yup.object({
  email: Yup.string().email("Please enter a valid email").required("Email is required"),
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  phone: Yup.string().required("Phone number is required"),
  property_address: Yup.string().required("Property address is required"),
});

function CheckIcon() {
  return (
    <img
      src={CheckCircle}
      alt=""
      className="w-5 h-5 flex-shrink-0 mt-0.5"
      style={{ filter: "invert(58%) sepia(80%) saturate(400%) hue-rotate(90deg) brightness(95%)" }}
    />
  );
}

function AssessmentField({ name, type = "text", placeholder, as }) {
  return (
    <div>
      <Field
        name={name}
        type={type}
        as={as}
        placeholder={placeholder}
        className="w-full bg-white border-0 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 tracking-wider"
        style={{ fontSize: "13px", fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
      />
      <ErrorMessage name={name} component="p" className="text-red-400 text-xs mt-1 text-left" />
    </div>
  );
}

export default function HomeMaintenanceAssessmentPage() {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    LeadMagnetApi.getCampaignPage(CAMPAIGN_SLUG)
      .then((data) => setPageContent(data))
      .catch(() => setFetchError("Unable to load the assessment page. Please try again later."))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      await LeadMagnetApi.submitCampaign(CAMPAIGN_SLUG, values);
      navigate("/home-maintenance-assessment/thank-you");
    } catch (err) {
      const data = err?.response?.data;
      const fields = ["email", "first_name", "last_name", "phone", "property_address"];
      const fieldWithError = fields.find((field) => data?.[field]);
      if (fieldWithError) {
        const message = data[fieldWithError];
        setFieldError(fieldWithError, Array.isArray(message) ? message[0] : message);
      } else {
        setFieldError("email", "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const defaultBullets = [
    "A practical look at the maintenance items that matter most for your home.",
    "Early warning signs that can help prevent expensive repairs.",
    "Helpful context for your roof, foundation, plumbing, electrical, and HVAC systems.",
    "A simple way to prioritize what needs attention now versus later.",
    "Answers from a Properteasy professional instead of guesswork from online searches.",
    "No obligation to book work—we will simply help you understand your next steps.",
  ];

  const apiBullets = pageContent
    ? [
        pageContent.bullet1,
        pageContent.bullet2,
        pageContent.bullet3,
        pageContent.bullet4,
        pageContent.bullet5,
        pageContent.bullet6,
      ].filter(Boolean)
    : [];
  const bullets = apiBullets.length > 0 ? apiBullets : defaultBullets;
  const leftBullets = bullets.slice(0, 3);
  const rightBullets = bullets.slice(3, 6);

  const scrollToForm = () => {
    document.getElementById("assessment-form")?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <p className="text-white text-lg">{fetchError}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col font-sans">
      <header className="bg-white border-b border-gray-200 py-3 px-6 flex justify-center">
        <img src={LogoFull} alt="PropertEasy" className="h-14 w-auto" />
      </header>

      <section
        className="relative flex items-center min-h-[calc(100vh-82px)] pb-12 px-4"
        style={{
          backgroundImage: `url(${HeroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.70)" }} />

        <div
          className="relative z-10 max-w-4xl mx-auto w-full"
          style={{ fontFamily: "'Inter', sans-serif", paddingTop: "20px" }}
        >
          <h1
            className="text-[34px] sm1:text-[40px] md:text-[52px] lg:text-[60px] font-bold text-white mb-10 text-center uppercase"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, lineHeight: 1.0 }}
          >
            {pageContent?.title || "FREE HOME MAINTENANCE ASSESSMENT"}
          </h1>

          <div className="flex flex-col items-center text-center mb-10">
            <p
              className="mb-5 text-white"
              style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", fontSize: "24px", fontWeight: 400, lineHeight: 1.2 }}
            >
              {pageContent?.subtitleLine1 || "Know what your home needs next."}
            </p>
            <p
              className="mb-5 text-white"
              style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", fontSize: "24px", fontWeight: 400, lineHeight: 1.2 }}
            >
              {pageContent?.subtitleLine2 || "Get a practical maintenance plan at no cost."}
            </p>
            <p
              className="text-white opacity-90"
              style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", fontSize: "22px", fontWeight: 400, lineHeight: 1.5, maxWidth: "650px" }}
            >
              {pageContent?.subtitleLine3 || "Tell us a little about your property and a Properteasy professional will follow up with helpful next steps."}
            </p>
          </div>

          <Formik
            initialValues={{
              email: "",
              first_name: "",
              last_name: "",
              phone: "",
              property_address: "",
            }}
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form id="assessment-form" className="space-y-3 max-w-xl mx-auto">
                <AssessmentField name="email" type="email" placeholder="Email Address" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <AssessmentField name="first_name" placeholder="First Name" />
                  <AssessmentField name="last_name" placeholder="Last Name" />
                </div>
                <div>
                  <Field name="phone">
                    {({ field }) => (
                      <InputMask {...field} mask="(999) 999-9999" maskChar="">
                        {(inputProps) => (
                          <input
                            {...inputProps}
                            type="tel"
                            placeholder="Phone Number"
                            className="w-full bg-white border-0 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 tracking-wider"
                            style={{ fontSize: "13px", fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                          />
                        )}
                      </InputMask>
                    )}
                  </Field>
                  <ErrorMessage name="phone" component="p" className="text-red-400 text-xs mt-1 text-left" />
                </div>
                <AssessmentField name="property_address" placeholder="Property Address" />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white font-semibold py-3 rounded tracking-wider transition-opacity disabled:opacity-60"
                  style={{ backgroundColor: "#52C271", fontSize: "14px", fontFamily: "'Inter', sans-serif" }}
                >
                  {isSubmitting ? "Sending..." : pageContent?.ctaButtonText || "Request My Free Assessment"}
                </button>

                <p className="text-center text-white" style={{ fontSize: "11px" }}>
                  Your information will be safely managed according to our{" "}
                  <a href="https://properteasy.com/legal" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
                    Privacy Policy
                  </a>
                  .
                </p>
              </Form>
            )}
          </Formik>
        </div>
      </section>

      <section className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-2xl md:text-3xl font-semibold text-gray-800 mb-14">
            {pageContent?.sectionTitle || "What Your Free Assessment Can Help With"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            <div className="space-y-8">
              {leftBullets.map((bullet, i) => (
                <div key={i} className="flex items-start gap-4">
                  <CheckIcon />
                  <p className="text-gray-600 text-sm leading-relaxed">{bullet}</p>
                </div>
              ))}
            </div>
            <div className="space-y-8">
              {rightBullets.map((bullet, i) => (
                <div key={i} className="flex items-start gap-4">
                  <CheckIcon />
                  <p className="text-gray-600 text-sm leading-relaxed">{bullet}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 px-6" style={{ backgroundColor: "#52C271" }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-6">
          <div className="text-white text-center max-w-sm">
            <h3 className="text-xl font-bold mb-2">{pageContent?.bannerHeadline || "Take the guesswork out of home maintenance."}</h3>
            <p className="text-white text-sm">{pageContent?.bannerSubtext || "Request your free assessment and get clear next steps for your property."}</p>
          </div>
          <button
            onClick={scrollToForm}
            className="bg-white font-semibold px-12 py-4 rounded text-base whitespace-nowrap flex-shrink-0"
            style={{ color: "#1a1a1a" }}
          >
            {pageContent?.bannerButtonText || "Request My Free Assessment"}
          </button>
        </div>
      </section>

      <footer className="bg-white border-t border-gray-100 py-6 px-6">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-3">
          <img src={Logo} alt="PropertEasy" className="h-7 w-auto" />
          <p className="text-gray-400 text-xs text-center">&copy;2026 Properteasy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
